import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature fleet-profile
import * as A from '../actions';
import { truckReducer } from './truck';
import { driverReducer } from './driver';
import { vendorReducer } from './vendor';
//////////////////////////////////////////////////

const initialPagination = {
  limit: 10,
  offset: 0,
};

const reportInitialState = {
  loading: {},
  totalCount: {},
  tableTitleFilters: {},
  tableTitleSortValues: {},
  pagination: {
    accessorialList: initialPagination,
    compensationList: initialPagination,
    unavailablePeriodList: initialPagination,
    payrollAccessorialList: initialPagination,
  },
};

const initialState = {
  type: null,
  vinLookup: false,
  configGroup: null,
  collapsedGroup: {},
  branchConfigs: null,
  accessorialConfigs: null,
  // fleet
  truck: {},
  trailer: {},
  vendor: reportInitialState,
  driver: {
    ...reportInitialState,
    resetAssignmentHistory: {
      truck: false,
      vendor: false,
      trailer: false,
      teamDriver: false,
    },
  },
};

const setInitialState = (state, type) => R.assoc(GC.FIELD_TYPE, type, initialState);

// users
const getGeneralUserListSuccess = (state, data) => P.$set('generalUserList', data, state);

const getFleetDispatchingGroupSuccess = (state, data) => (
  P.$set('dispatchingGroup', data, state)
);

// common
const getGeneralDetailsSuccess = (state, data) => P.$set(`${state.type}.generalDetails`, data, state);

const toggleFormGroupTable = (state, groupName) => (
  P.$set(
    `collapsedGroup.${groupName}`,
    R.not(R.pathOr(true, ['collapsedGroup', ...R.split('.', groupName)], state)),
    state,
  )
);

const setVinLookup = (state, value) => P.$toggle('vinLookup', value, state);

const toggleFormGroupTableDetails = (state, { guid, groupName }) => {
  const { type } = state;

  const list = R.path([type, groupName], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);
  const path = `${type}.${groupName}.${index}.expanded`;

  return P.$set(path, R.not(R.path(R.split('.', path), state)), state);
};

const getItemListSuccess = (state, { data, groupName, withPagination }) => {
  const { type } = state;

  const itemList = R.pathOr(data, ['results'], data);

  if (R.not(withPagination)) {
    if (G.isNilOrEmpty(itemList)) return P.$set(`${type}.${groupName}`, [], state);

    let listToUse = G.ifElse(G.isArray(itemList), itemList, R.of(Array, itemList));

    if (R.equals(groupName, 'advancePaymentList')) {
      listToUse = R.map(
        (entity) => {
          const { status, integrationType } = entity;

          if (R.and(
              R.equals(status, GC.ADVANCE_PAYMENT_STATUS_CANCELLED),
              R.equals(integrationType, GC.ADVANCE_PAYMENT_INTEGRATION_TYPE_RELAY),
            )) return R.assoc('disabled', true, entity);

          return entity;
        },
        listToUse,
      );
    }

    return P.$set(`${type}.${groupName}`, listToUse, state);
  }

  const totalCount = R.pathOr(0, [type, 'totalCount', groupName], state);
  const sum = R.sum(R.values(R.pathOr({}, [type, 'pagination', groupName], state)));

  const offset = G.ifElse(R.gt(totalCount, sum), sum, totalCount);

  const newItemList = R.concat(R.pathOr([], [type, groupName], state), itemList);

  if (G.isNilOrEmpty(newItemList)) return P.$set(`${type}.${groupName}`, [], state);

  return P.$all(
    P.$set(`${type}.${groupName}`, newItemList),
    P.$set(`${type}.totalCount.${groupName}`, totalCount),
    P.$set(`${type}.pagination.${groupName}.offset`, offset),
    state,
  );
};

const getReportItemListSuccess = (state, { data, groupName }) => {
  const { type } = state;

  const { results, totalCount } = data;

  const sum = R.sum(R.values(R.pathOr({}, [type, 'pagination', groupName], state)));

  const offset = G.ifElse(R.gt(totalCount, sum), sum, totalCount);

  const itemList = R.concat(R.pathOr([], [type, groupName], state), results);

  if (G.isNilOrEmpty(itemList)) return P.$set(`${type}.${groupName}`, [], state);

  return P.$all(
    P.$set(`${type}.${groupName}`, itemList),
    P.$set(`${type}.totalCount.${groupName}`, totalCount),
    P.$set(`${type}.pagination.${groupName}.offset`, offset),
    state,
  );
};

const createOrUpdateEntitySuccess = (state, { data, groupName }) => {
  const { type } = state;

  const guid = G.getGuidFromObject(data);
  const list = R.path([type, groupName], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.equals(index, -1)) return P.$add(`${type}.${groupName}`, data, state);

  return P.$set(`${type}.${groupName}.${index}`, data, state);
};

const removeEntitySuccess = (state, { guid, groupName }) => {
  const { type } = state;

  const list = P.$get(`${type}.${groupName}`, state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${type}.${groupName}.${index}`, state);
};

const makeDropdownOptions = (dropdowns) => R.compose(
  R.reduce((acc, dropdownName) => R.assoc(
    dropdownName,
    G.createOptionsFromDropdownConfigWithGuidOrParentGuid({ dropdowns }, dropdownName, true),
    acc,
  ), {}),
  R.keys,
)(dropdowns);

// configs
const getFleetProfileConfigsSuccess = (state, data) => {
  const { configGroup } = state;

  const { configs, dropdowns } = data;

  const dropdownOptions = makeDropdownOptions(dropdowns);

  if (R.isNil(configGroup)) {
    return P.$set('configGroup', { configs, dropdownOptions }, state);
  }

  const { configs: existentConfigs, dropdownOptions: existentDropdownOptions } = configGroup;

  const newConfigGroup = {
    configs: R.mergeRight(existentConfigs, configs),
    dropdownOptions: R.mergeRight(existentDropdownOptions, dropdownOptions),
  };

  return P.$set('configGroup', newConfigGroup, state);
};

const getBranchConfigsSuccess = (state, data) => {
  const { configs, dropdowns } = data;

  const dropdownOptions = makeDropdownOptions(dropdowns);

  return P.$set('branchConfigs', { configs, dropdownOptions }, state);
};

// working division

const changeAssignedToDivisionSuccess = (state, data) => {
  const { type, assignedToDivisionGuid } = data;

  const generalDetails = R.path([type, 'generalDetails'], state);

  const { version } = generalDetails;

  const newGeneralDetails = R.mergeRight(
    generalDetails,
    { assignedToDivisionGuid, [GC.FIELD_VERSION]: R.inc(version) },
  );

  return P.$set(`${type}.generalDetails`, newGeneralDetails, state);
};

// vendor
const getFleetVendorListSuccess = (state, data) => P.$set('fleetVendorList', data, state);

// assign vendor
const assignOrUnAssignVendorSuccess = (state, data) => {
  const { type, unassign, vendorGuid } = data;

  const generalDetails = R.path([type, 'generalDetails'], state);

  const { version } = generalDetails;

  const fleetVendorGuid = G.ifElse(unassign, null, vendorGuid);
  const newGeneralDetails = R.mergeRight(generalDetails, { fleetVendorGuid, [GC.FIELD_VERSION]: R.inc(version) });

  return P.$set(`${type}.generalDetails`, newGeneralDetails, state);
};

// accessorial configs
const getAccessorialConfigListSuccess = (state, data) => {
  const accessorialConfigList = R.compose(
    R.indexBy(R.prop(GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID)),
    R.map((item) => R.compose(
      R.assoc(GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID, R.prop(GC.FIELD_ORIGINAL_CONFIG_GUID, item)),
      R.pick([
        GC.FIELD_NON_TAXABLE,
        GC.FIELD_FUEL_RELATED,
        GC.FIELD_DISPLAYED_VALUE,
        GC.FIELD_CHARGE_RATE_TYPE,
        GC.FIELD_CHARGE_RATE_UNIT,
      ]),
    )(item)),
  )(data);

  const accessorialConfigOptions = R.map(({ displayedValue, originalConfigGuid }) => ({
    [GC.FIELD_LABEL]: displayedValue,
    [GC.FIELD_VALUE]: originalConfigGuid,
  }), data);

  G.setItemToWindow('accessorialConfigOptions', accessorialConfigOptions);

  return P.$set('accessorialConfigs', { accessorialConfigList, accessorialConfigOptions }, state);
};

// filters
const setDocumentsFilter = (state, filter) => (
  P.$set(`${state.type}.formGroupFilters.documentsFilter`, filter, state)
);

// report
const setPagination = (state, { groupName, totalCount, increase, decrease }) => {
  const { type } = state;

  let offset = R.pathOr(0, [type, 'pagination', groupName, 'offset'], state);

  if (G.isNotNil(totalCount)) {
    offset = G.ifElse(
      R.gt(R.add(offset, 10), totalCount),
      totalCount,
      R.gt(R.add(offset, 10), totalCount),
    );
  }

  if (increase) offset = R.inc(offset);

  if (decrease) offset = R.dec(offset);

  return P.$set(`${type}.pagination.${groupName}.offset`, offset, state);
};

const setTableTitleSortValues = (state, { data, groupName }) => {
  const { type } = state;

  const path = `${type}.tableTitleSortValues.${groupName}`;

  const titleSortValues = R.pathOr({}, R.split('.', path), state);

  const sortValues = G.getTableTitleSortValues(titleSortValues, data);

  return P.$set(path, sortValues, state);
};

const setTableTitleFilter = (state, { data, groupName }) => {
  const { type } = state;

  const path = `${type}.tableTitleFilters.${groupName}`;

  const tableTitleFilters = R.pathOr({}, R.split('.', path), state);

  const filterValues = G.getTableTitleFilterValues(tableTitleFilters, data);

  return P.$set(path, filterValues, state);
};

const resetListAndPagination = (state, { groupName, shouldResetTotal }) => {
  const { type } = state;

  let nextState = P.$all(
    P.$set(`${type}.${groupName}`, null),
    P.$set(`${type}.pagination.${groupName}`, initialPagination),
    state,
  );

  if (shouldResetTotal) {
    nextState = P.$set(`${type}.totalCount.${groupName}`, null, nextState);
  }

  return nextState;
};

const setListLoading = (state, { loading, groupName }) => (
  P.$set(`${state.type}.loading.${groupName}`, loading, state)
);

const setReports = (state, { reports, groupName }) => (
  P.$set(`${state.type}.availableReports.${groupName}`, reports, state)
);

const setUsedReport = (state, { groupName, usedReport }) => (
  P.$all(
    P.$set(`${state.type}.${groupName}`, []),
    P.$set(`${state.type}.reportPending.${groupName}`, false),
    P.$set(`${state.type}.tableTitleFilters.${groupName}`, {}),
    P.$set(`${state.type}.usedReport.${groupName}`, usedReport),
    P.$set(`${state.type}.tableTitleSortValues.${groupName}`, {}),
    P.$set(`${state.type}.pagination.${groupName}`, initialPagination),
    state,
  )
);

const setReportPending = (state, { groupName }) => (
  P.$set(`${state.type}.reportPending.${groupName}`, true, state)
);

const setTotalCount = (state, { data, groupName }) => (
  P.$set(`${state.type}.totalCount.${groupName}`, data, state)
);

export default createReducer({
  [A.setVinLookup]: setVinLookup,
  [A.setTotalCount]: setTotalCount,
  [A.setInitialState]: setInitialState,
  [A.getItemListSuccess]: getItemListSuccess,
  [A.removeEntitySuccess]: removeEntitySuccess,
  [A.toggleFormGroupTable]: toggleFormGroupTable,
  [A.getGeneralDetailsSuccess]: getGeneralDetailsSuccess,
  [A.toggleFormGroupTableDetails]: toggleFormGroupTableDetails,
  [A.createOrUpdateEntitySuccess]: createOrUpdateEntitySuccess,
  // users
  [A.getGeneralUserListSuccess]: getGeneralUserListSuccess,
  [A.getFleetDispatchingGroupSuccess]: getFleetDispatchingGroupSuccess,
  // configs
  [A.getBranchConfigsSuccess]: getBranchConfigsSuccess,
  [A.getFleetProfileConfigsSuccess]: getFleetProfileConfigsSuccess,
  // working division
  [A.changeAssignedToDivisionSuccess]: changeAssignedToDivisionSuccess,
  // vendor
  [A.getFleetVendorListSuccess]: getFleetVendorListSuccess,
  // assign vendor
  [A.assignOrUnAssignVendorSuccess]: assignOrUnAssignVendorSuccess,
  // accessorial configs
  [A.getAccessorialConfigListSuccess]: getAccessorialConfigListSuccess,
  // filters
  [A.setDocumentsFilter]: setDocumentsFilter,
  // report
  [A.setReports]: setReports,
  [A.setUsedReport]: setUsedReport,
  [A.setPagination]: setPagination,
  [A.setListLoading]: setListLoading,
  [A.setReportPending]: setReportPending,
  [A.setTableTitleFilter]: setTableTitleFilter,
  [A.resetListAndPagination]: resetListAndPagination,
  [A.setTableTitleSortValues]: setTableTitleSortValues,
  [A.getReportItemListSuccess]: getReportItemListSuccess,
  // truck
  ...truckReducer,
  // driver
  ...driverReducer,
  // vendor
  ...vendorReducer,
}, initialState);
